import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Loader2, KeyRound } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token");

  const [step, setStep] = useState(urlToken ? "reset" : "request");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(urlToken || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password/forgot", { email });
      setStep("sent");
      toast.success("If the address exists, a reset link was sent.");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e?.preventDefault();
    if (newPassword.length < 8) return toast.error("Password must be at least 8 chars.");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, new_password: newPassword });
      setStep("done");
      toast.success("Password updated. You can now sign in.");
    } catch (err) {
      toast.error(errorMessage(err)?.message || "Reset failed. Token may be invalid or expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm border-border shadow-none p-8" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={12} /> Back to sign in
        </Link>

        <div className="w-8 h-8 rounded-md bg-foreground text-background flex items-center justify-center mb-4">
          <KeyRound size={16} />
        </div>

        {step === "request" && (
          <>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">Reset password</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Enter your email and we&rsquo;ll send you a reset link.
            </p>
            <form onSubmit={request} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                  data-testid="forgot-email"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy || !email} data-testid="forgot-submit">
                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Send reset link
              </Button>
            </form>
          </>
        )}

        {step === "sent" && (
          <>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">Check your email</h1>
            <p className="text-sm text-muted-foreground mb-6">
              If an account exists for <span className="font-medium">{email}</span>, we have sent a password reset link. 
              Please check your inbox and click the link to continue.
            </p>
            <Link to="/login">
              <Button className="w-full" variant="outline" data-testid="forgot-back-to-login">Back to sign in</Button>
            </Link>
          </>
        )}

        {step === "reset" && (
          <>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">Enter Password</h1>
            <p className="text-sm text-muted-foreground mb-4">
              Enter your new password below.
            </p>
            <form onSubmit={reset} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">New password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} data-testid="reset-password" />
                <div className="text-[10px] text-muted-foreground">Minimum 8 characters.</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confirm new password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} data-testid="reset-confirm-password" />
              </div>
              <Button type="submit" className="w-full" disabled={busy || !token || newPassword.length < 8 || newPassword !== confirmPassword} data-testid="reset-submit">
                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Update password
              </Button>
            </form>
          </>
        )}

        {step === "done" && (
          <>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">All set</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link to="/login">
              <Button className="w-full" data-testid="forgot-back-to-login">Back to sign in</Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
